<script lang="ts">
  import { onMount } from 'svelte';
  import { fade, slide } from 'svelte/transition';
  import Icon from '../components/Icon.svelte';
  import { getCMSComponentsFromHybrisAsync } from '../services/hybris';

  export let metaData;

  let loaded = false;
  let accordionData = [];
  let openIndexes: number[] = [];
  let activeIndex = -1;
  let accordionElement: HTMLDivElement;

  export function onLoad() {
    const cmsLinks = metaData?.accordionItems?.split(' ');
    getCMSComponentsFromHybrisAsync(cmsLinks).then(res => {
      loaded = true;
      accordionData = res.component;
    });
  }

  function toggleItem(index: number) {
    const isCurrentlyOpen = openIndexes.includes(index);
    openIndexes = isCurrentlyOpen ? openIndexes.filter(i => i !== index) : [...openIndexes, index];
    activeIndex = index;
  }

  onMount(() => {
    function handleOutsideClick(event: MouseEvent) {
      const clickedInsideAccordionList = (event.target as HTMLElement)?.closest('.extra-accordion__list');

      if (accordionElement && !clickedInsideAccordionList) {
        activeIndex = -1;
      }
    }
    document.addEventListener('click', handleOutsideClick);
    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  });
</script>

{#if loaded}
  <div class="extra-accordion" bind:this={accordionElement}>
    {#each accordionData as item, index (index)}
      <div class="extra-accordion__list">
        <div class="extra-accordion__item" class:open={openIndexes.includes(index)}>
          <h4 class="extra-accordion__item-title">
            <button
              id={`extra-accordion__heading_${index}`}
              type="button"
              class:collapsed={!openIndexes.includes(index)}
              class:is-open={openIndexes.includes(index)}
              class:is-active={activeIndex === index}
              class="extra-accordion__trigger"
              aria-controls={`extra-accordion__content_${index}`}
              aria-expanded={openIndexes.includes(index)}
              on:click={() => toggleItem(index)}
            >
              <span class="extra-accordion__heading">{item.title}</span>
              <span class="extra-accordion__icon" class:rotate180={openIndexes.includes(index)} aria-hidden="true">
                <Icon type="font" glyph="chevron_down" mobileSize={24} desktopSize={24} />
              </span>
            </button>
          </h4>

          {#if openIndexes.includes(index)}
            <div
              id={`extra-accordion__content_${index}`}
              class="extra-accordion__content"
              role="region"
              aria-labelledby={`extra-accordion__heading_${index}`}
              in:slide={{ duration: 220, axis: 'y' }}
              out:fade={{ duration: 160 }}
            >
              {@html item.description}
            </div>
          {/if}
        </div>
      </div>
    {/each}
  </div>
{/if}

<style lang="scss">
  @import '../styles/colors.new.scss';

  .extra-accordion {
    width: 100%;
    padding: 33px 16px;
    @include desktop-min-width {
      padding: 48px;
    }
    &__title {
      color: $neutral-dark-01;
      font-size: 14px;
      font-weight: 400;
      line-height: 16px;
      margin: 0 0 10px;
    }

    &__list {
      display: flex;
      flex-direction: column;
      gap: 14px;
      margin-bottom: 10px;
    }

    &__item {
      background-color: $primary-white;
      border: 1px solid $neutral-dark-05;
      border-radius: 12px;
      overflow: hidden;
      margin-bottom: 10px;
    }

    &__item-title {
      margin: 0;
    }

    &__trigger {
      align-items: center;
      background: transparent;
      border: 0;
      cursor: pointer;
      display: flex;
      gap: 18px;
      justify-content: space-between;
      padding: 20px 25px;
      position: relative;
      text-align: left;
      width: 100%;

      &.is-open {
        border-bottom: 1px solid $neutral-dark-05;
        border-radius: 12px;
      }

      &.is-active {
        background-color: $neutral-light-04;
      }

      &:hover {
        background-color: $neutral-light-04;
      }

      &:hover .extra-accordion__heading,
      &.is-active .extra-accordion__heading {
        color: $primary-blue;
      }
    }

    &__heading {
      color: $neutral-dark-01;
      font-size: 14px;
      font-weight: 500;
      line-height: 25px;
      padding-inline-end: 44px;
      transition: color 0.2s ease;
    }

    &__icon {
      color: $icon-only-icon-blue;
      flex-shrink: 0;
      position: absolute;
      right: 28px;
      transition: transform 250ms ease;

      &.rotate180 {
        transform: rotate(180deg);
      }

      @include responsive {
        right: 18px;
        top: 20px;
      }
    }

    &__content {
      color: $neutral-dark-01;
      padding: 25px;
      transform-origin: top;
    }
  }
</style>
